import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { theme } from 'assets/styles/theme';
import editIcon from 'assets/svg/edit-icon.svg';
import { Text1Bold, Text2 } from 'components/typography/Texts';
import { useVerifyPhonePopup } from 'features/account/verify-phone/useVerifyPhonePopup';
import { useUpdateEmail } from 'features/my-profile/account-settings/hooks/useUpdateEmail';
import { useUpdateMyProfileDetails } from 'features/my-profile/account-settings/hooks/useUpdateMyProfileDetails';

import { ContentSection } from './ContentSection';

const Container = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${theme.color.borderGray};
  padding: 10px 0;
`;

const Label = styled(Text2)`
  width: 120px;
  ${theme.mq.tablet} {
    width: 80px;
  }
`;

const EditText = styled(Text1Bold)`
  color: ${theme.color.red};
`;

const EditContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
`;

type FieldType = 'firstName' | 'lastName' | 'about' | 'phone' | 'email' | 'new-email';

interface UserDataInputProps {
  label: string;
  value: string;
  field: FieldType;
  onConfirmed?: () => void;
  canEdit?: boolean;
  confirmEmail?: boolean;
}

export const UserDataInput = ({ label, value, field, onConfirmed, canEdit }: UserDataInputProps) => {
  const { t } = useTranslation('myProfile');
  const { updateMyProfileDetails, isLoading } = useUpdateMyProfileDetails();
  const { editEmailAndShowConfirmationPopup, isLoading: isEmailLoading, showEmailConfirmationPopup } = useUpdateEmail();

  const [inputValue, setInputValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const { showVerifyPhonePopup } = useVerifyPhonePopup();
  const isAnyLoading = isLoading || isEmailLoading;

  const isEditable = field !== 'email' || canEdit;
  const hasChanges = inputValue !== value;

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleEditOrSaveClick = async () => {
    if (field === 'phone') {
      showVerifyPhonePopup(onConfirmed);
      return;
    }

    if (field === 'email') {
      setIsEditing(!isEditing);

      if (isEditing && hasChanges) {
        await editEmailAndShowConfirmationPopup(inputValue, {
          onPopupClose: () => {
            setInputValue(value);
            setIsEditing(false);
          },
        });
      }
      return;
    }

    if (field === 'new-email') {
      showEmailConfirmationPopup(inputValue, {
        onPopupClose: () => {
          setIsEditing(false);
        },
      });
    }

    if (isEditing && hasChanges) {
      const payload = { [field]: inputValue };
      await updateMyProfileDetails(payload);
    }
    setIsEditing(!isEditing);
  };

  const handleChange = (value: string) => {
    setInputValue(value);
  };

  return (
    <Container data-testid={`user-data-${field}`}>
      <Label>{label}</Label>
      <ContentSection
        isLoading={isAnyLoading}
        isEditing={isEditing}
        inputValue={inputValue}
        onChange={handleChange}
        fieldType={field}
      />
      {isEditable && field !== 'new-email' && (
        <EditContainer data-testid={'edit-btn'} onClick={() => handleEditOrSaveClick()}>
          <img src={editIcon} alt={'edit icon'} />
          <EditText>{isEditing ? t('account-settings.save') : t('account-settings.edit')}</EditText>
        </EditContainer>
      )}
      {field === 'new-email' && (
        <EditContainer data-testid={'confirm-btn'} onClick={() => handleEditOrSaveClick()}>
          <EditText>{t('account-settings.confirm')}</EditText>
        </EditContainer>
      )}
    </Container>
  );
};
